(() => {
  // ===== SHARED HELPERS =====
  const $ = (sel, root) => root.querySelector(sel);
  const $$ = (sel, root) => Array.from(root.querySelectorAll(sel));
  const toCents = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const money = (c) => (c == null ? '' : '£' + (c / 100).toFixed(2));
  const fromMoney = (c) => (c == null ? '' : `From ${money(c)}`);
  const show = (el, on) => { if (el) el.style.display = on ? '' : 'none'; };
  const clamp = (i, len) => Math.max(0, Math.min(i, Math.max(0, len - 1)));
  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const BAG_SVG = `
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M7 8V7a5 5 0 0 1 10 0v1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      <path d="M6 8h12l-1 13H7L6 8Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
    </svg>
  `;

  const btnClass = ({ isFrequency = false } = {}) =>
    `w-100${isFrequency ? ' tcm-radio-btn' : ' tcm-tile-radio-btn'}`;

  // ===== SHARED CART STATE =====
  let cartVariantIds = new Set();
  const instances = []; // all initialized form instances

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  function updateCartVariantIds(cart) {
    if (!cart || !cart.items) return;
    cartVariantIds = new Set(cart.items.map(item => String(item.variant_id)));
  }

  async function checkAllCarts() {
    const cart = await fetchCart();
    if (cart) {
      updateCartVariantIds(cart);
      instances.forEach(inst => inst.updateAtcButtonState());
    }
  }

  function setupCartListeners() {
    document.addEventListener('cart:updated', () => checkAllCarts());
    document.addEventListener('rebuy:cart.change', () => checkAllCarts());
    document.addEventListener('tcm:cart:refresh', () => checkAllCarts());

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      return originalFetch.apply(this, args).then(response => {
        if (typeof url === 'string' && (
          url.includes('/cart/add') ||
          url.includes('/cart/update') ||
          url.includes('/cart/change') ||
          url.includes('/cart/clear')
        )) {
          setTimeout(() => checkAllCarts(), 100);
        }
        return response;
      });
    };

    setInterval(() => checkAllCarts(), 2000);
  }

  // ===== PER-FORM INITIALIZATION =====
  function initProductForm(scope) {
    // Read config from embedded JSON
    const dataEl = scope.querySelector('script.tcm-product-data');
    if (!dataEl) return;

    let config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('TCM: Failed to parse product data for scope', scope, e);
      return;
    }

    const pd = {
      sellingPlanGroups: config.sellingPlanGroups || [],
      variants: config.variants || [],
      variantMeta: config.variantMeta || {}
    };

    // ---------- per-instance config ----------
    const defaultSubIndex = Math.max(0, Number(config.defaultSubTile || 1) - 1);
    const defaultGroupIndex = Math.max(0, Number(config.defaultGroup || 1) - 1);
    const defaultVariantIndex = Math.max(0, Number(config.defaultVariant || 1) - 1);
    const enableAlreadyInCartLimit = config.enableAlreadyInCartLimit !== false;
    const currentProductId = String(config.currentProductId || '');
    const productOutOfStock = config.productOutOfStock === true;
    const showGroups = config.showGroups === true;
    const subscribeLayoutMode = String(config.subscribeLayoutMode || '');
    const defaultOutOfStockMessage = 'This product is no longer available to purchase.';
    const subscriptionNote = String(config.subscriptionNote || '').trim();
    const subscriptionNoteHtml = subscriptionNote
      ? `<small class="tcm-frequency-note d-block">${escapeHtml(subscriptionNote)}</small>`
      : '';
    const formatFrequencyLabel = (name) => {
      const label = String(name || 'Subscription').trim();
      if (!label) return 'Every 1 Month';
      return /^every\b/i.test(label) ? label : `Every ${label}`;
    };
    const normalizeFilterToken = (value) =>
      String(value || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, ' and ')
        .replace(/['']/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    const rawFilterTokens = String(config.variantFilterRaw || '')
      .split(/[,;\n|]+/)
      .map(normalizeFilterToken)
      .filter(Boolean);
    const frequencyFilterTokens = new Set(
      [...rawFilterTokens, ...((config.variantFilterTokens || []))]
        .map(normalizeFilterToken)
        .filter(Boolean)
    );
    const hasFrequencyFilterActive = frequencyFilterTokens.size > 0;
    const matchesFrequencyFilter = (label) => {
      if (!frequencyFilterTokens.size) return true;
      const withEvery = normalizeFilterToken(label);
      const withoutEvery = withEvery.replace(/^every-/, '');
      if (!withoutEvery) return false;
      const candidates = [
        withEvery,
        withoutEvery,
        withoutEvery.replace(/-months$/, '-month'),
        withoutEvery.replace(/-weeks$/, '-week'),
        withoutEvery.replace(/-days$/, '-day')
      ].filter(Boolean);
      return Array.from(frequencyFilterTokens).some(token =>
        candidates.some(candidate =>
          candidate === token || candidate.includes(token) || token.includes(candidate)
        )
      );
    };

    // ---------- scoped elements ----------
    const els = {
      subscriptionUi: $('[data-tcm-subscription-ui]', scope),
      groupContainer: $('[data-tcm-group-list]', scope)?.closest('.omni-group-list-container'),
      groupList: $('[data-tcm-group-list]', scope),
      variantList: $('[data-tcm-variant-list]', scope),
      subList: $('[data-tcm-sub-list]', scope),
      oneTimeList: $('[data-tcm-one-time-list]', scope),

      subPricesVariant: $('[data-tcm-sub-prices="variant"]', scope),
      subPricesSub: $('[data-tcm-sub-prices="sub"]', scope),

      subscribeVariantContainer: $('[data-tcm-subscribe-container="variant"]', scope),
      subscribeSubContainer: $('[data-tcm-subscribe-container="sub"]', scope),
      oneTimeContainer: $('[data-tcm-onetime-container]', scope),

      subscribeBenefits: $$('[data-tcm-subscribe-benefits]', scope),

      variantId: $('[name="id"]', scope),
      sellingPlan: $('[name="selling_plan"]', scope),
      atcBtn: $('[data-tcm-atc]', scope),
      stockAlert: $('[id^="tcm-stock-alert"]', scope) || $('.omni-alert', scope),
    };
    if (!els.variantId || !els.sellingPlan || !els.atcBtn) return;
    const hasRealGroups = els.subscriptionUi?.dataset?.hasRealGroups === 'true';

    // ---------- cart checking (per-instance) ----------
    let selectionIsOutOfStock = false;

    function isProductInCart() {
      const allVariantIds = (pd.variants || []).map(v => String(v.id));
      return allVariantIds.some(id => cartVariantIds.has(id));
    }

    function isSelectionOutOfStock() {
      const selectedVariantId = String(currentPurchaseItem()?.variantId || '').trim();
      const selectedVariantMeta = selectedVariantId ? (pd.variantMeta?.[selectedVariantId] || {}) : {};
      return (
        (selectedVariantId && selectedVariantMeta.outOfStock === true) ||
        (currentProductId && productOutOfStock)
      );
    }

    function updateStockAlert(isOutOfStock) {
      if (!els.stockAlert) return;
      if (isOutOfStock) {
        const fallback = (els.stockAlert.getAttribute('data-fallback') || '').trim();
        const msg = fallback || defaultOutOfStockMessage;
        const textEl = els.stockAlert.querySelector('small');
        if (textEl) textEl.textContent = msg;
        show(els.stockAlert, true);
        return;
      }
      show(els.stockAlert, false);
    }

    function updateAtcButtonState() {
      if (!els.atcBtn) return;
      if (selectionIsOutOfStock) {
        els.atcBtn.disabled = true;
        els.atcBtn.setAttribute('aria-disabled', 'true');
        els.atcBtn.style.opacity = '0.5';
        els.atcBtn.style.cursor = 'not-allowed';
        els.atcBtn.textContent = 'Out of stock';
        updateStockAlert(true);
        return;
      }

      updateStockAlert(false);
      els.atcBtn.disabled = false;
      els.atcBtn.removeAttribute('aria-disabled');
      els.atcBtn.style.opacity = '';
      els.atcBtn.style.cursor = '';

      if (!enableAlreadyInCartLimit) {
        paintPricesAndAtc();
        return;
      }

      const inCart = isProductInCart();
      els.atcBtn.disabled = inCart;

      if (inCart) {
        els.atcBtn.setAttribute('aria-disabled', 'true');
        els.atcBtn.style.opacity = '0.5';
        els.atcBtn.style.cursor = 'not-allowed';
        els.atcBtn.textContent = 'Already in basket';
      } else {
        els.atcBtn.removeAttribute('aria-disabled');
        els.atcBtn.style.opacity = '';
        els.atcBtn.style.cursor = '';
        paintPricesAndAtc();
      }
    }

    // ---------- normalize product data ----------
    const planNameById = Object.fromEntries(
      (pd.sellingPlanGroups || [])
        .flatMap(g => (g?.selling_plans || []).map(p => [String(p.id), p.name]))
    );

    const priceByVariantId = Object.fromEntries(
      (pd.variants || []).map(v => [
        String(v.id),
        { price: toCents(v.price), compare: toCents(v.compare_at_price) }
      ])
    );
    const variantById = Object.fromEntries(
      (pd.variants || []).map(v => [String(v.id), v])
    );

    const variantMeta = pd.variantMeta || {};

    const splitTitle = (t = '') => {
      const normalizedTitle = String(t || '').trim();
      const parts = normalizedTitle.split('/');
      const group = (parts.shift() || '').trim() || 'Unknown';
      const trailing = (parts.join('/') || '').trim();
      const label = trailing || group || normalizedTitle || 'Unknown';
      return { group, label };
    };

    const tmp = new Map();
    (pd.variants || []).forEach(v => {
      if (!v) return;
      const { group, label } = splitTitle(v.public_title);
      if (!tmp.has(group)) tmp.set(group, []);
      const plans = (v.selling_plan_allocations || []).map(spa => ({
        id: String(spa.selling_plan_id),
        name: planNameById[String(spa.selling_plan_id)] || '',
        price: toCents(spa.per_delivery_price ?? spa.price)
      }));
      tmp.get(group).push({ label, variantId: String(v.id), plans });
    });

    const groups = Array.from(tmp, ([name, items]) => ({ name, items }));

    const clampedGroup = clamp(defaultGroupIndex, groups.length);
    const clampedVariant = clamp(defaultVariantIndex, groups[clampedGroup]?.items?.length || 1);
    const groupHasFrequencyOptions = (group) => {
      const items = group?.items || [];
      return items.length > 1 && items.every(item => item?.plans && item.plans.length === 1);
    };
    const filteredVariantIndexesForGroup = (group) => {
      const items = group?.items || [];
      const all = items.map((_, idx) => idx);
      if (!frequencyFilterTokens.size || !groupHasFrequencyOptions(group)) return all;
      const matched = all.filter(idx => {
        const item = items[idx];
        const label = item?.plans?.[0]?.name || item?.label || '';
        return matchesFrequencyFilter(label);
      });
      return matched.length ? matched : all;
    };

    const hasAnyPlans = groups.some(g => g.items.some(it => (it.plans || []).length));
    const isItemInStock = (item) => {
      if (!item?.variantId) return false;
      const variantId = String(item.variantId);
      const meta = variantMeta[variantId] || {};
      const variant = variantById[variantId] || {};
      return meta.outOfStock !== true && variant.available !== false;
    };
    const findPreferredVariantIndex = (group, preferredIndex = 0) => {
      const candidateIndexes = filteredVariantIndexesForGroup(group);
      if (!candidateIndexes.length) return clamp(preferredIndex, group?.items?.length || 1);
      if (candidateIndexes.includes(preferredIndex) && isItemInStock(group?.items?.[preferredIndex])) {
        return preferredIndex;
      }
      const firstInStockIndex = candidateIndexes.find(index => isItemInStock(group?.items?.[index]));
      return firstInStockIndex ?? candidateIndexes[0];
    };
    const findPreferredInitialSelection = () => {
      const groupIndexes = groups.map((_, index) => index);
      const orderedGroupIndexes = [
        clampedGroup,
        ...groupIndexes.filter(index => index !== clampedGroup)
      ];

      for (const groupIndex of orderedGroupIndexes) {
        const group = groups[groupIndex];
        if (!group) continue;
        const preferredVariantIndex = groupIndex === clampedGroup ? clampedVariant : 0;
        const resolvedVariantIndex = findPreferredVariantIndex(group, preferredVariantIndex);
        if (isItemInStock(group.items?.[resolvedVariantIndex])) {
          return { groupIndex, variantIndex: resolvedVariantIndex };
        }
      }

      return {
        groupIndex: clampedGroup,
        variantIndex: findPreferredVariantIndex(groups[clampedGroup], clampedVariant)
      };
    };
    const initialSelection = findPreferredInitialSelection();
    const state = {
      g: initialSelection.groupIndex,
      v: initialSelection.variantIndex,
      s: defaultSubIndex,
      mode: hasAnyPlans ? 'sub' : 'one'
    };

    // ---------- computed getters ----------
    const curGroup = () => groups[clamp(state.g, groups.length)];
    const curItem = () => {
      const g = curGroup();
      if (!g) return null;
      state.v = clamp(state.v, g.items.length);
      return g.items[state.v] || null;
    };
    const curPlans = () => (curItem()?.plans || []);
    const findOneTimeItemForGroup = (group) => {
      const items = group?.items || [];
      if (!items.length) return null;
      if (!groupHasFrequencyOptions(group)) {
        return items[clamp(state.v, items.length)] || items[0] || null;
      }
      return items.reduce((best, candidate) => {
        if (!candidate?.variantId) return best;
        if (!best?.variantId) return candidate;

        const candidatePrice = priceByVariantId[candidate.variantId]?.price;
        const bestPrice = priceByVariantId[best.variantId]?.price;

        if (candidatePrice == null) return best;
        if (bestPrice == null) return candidate;
        return candidatePrice < bestPrice ? candidate : best;
      }, null) || items[0] || null;
    };
    const currentPurchaseItem = () => (
      state.mode === 'one'
        ? (findOneTimeItemForGroup(curGroup()) || curItem())
        : curItem()
    );
    const filteredPlanIndexes = (plans) => {
      const all = (plans || []).map((_, idx) => idx);
      if (!frequencyFilterTokens.size) return all;
      const matched = all.filter(idx => matchesFrequencyFilter(plans[idx]?.name || ''));
      return matched.length ? matched : all;
    };
    const renderFilterDebug = () => {
      const debugEnabled = config.filterDebugEnabled === true;
      const debugEl = $('[data-tcm-filter-debug-output]', scope);
      if (!debugEnabled || !debugEl) return;

      const currentGroup = curGroup();
      const currentPlans = curPlans();
      const currentPlanIndexes = filteredPlanIndexes(currentPlans);
      const currentPlanNames = currentPlans.map(p => p?.name || '').filter(Boolean);
      const selectedPlanNames = currentPlanIndexes.map(i => currentPlans[i]?.name || '').filter(Boolean);

      const groupLines = groups.map((group, groupIndex) => {
        const items = group?.items || [];
        const variantIndexes = filteredVariantIndexesForGroup(group);
        const labels = items.map(item => item?.plans?.[0]?.name || item?.label || '').filter(Boolean);
        const selectedLabels = variantIndexes.map(i => items[i]?.plans?.[0]?.name || items[i]?.label || '').filter(Boolean);
        const marker = groupIndex === state.g ? '*' : ' ';
        return `${marker} ${group.name}: ${selectedLabels.length}/${labels.length} visible | all=[${labels.join(' | ')}] | visible=[${selectedLabels.join(' | ')}]`;
      });

      const lines = [
        `raw_filter="${String(config.variantFilterRaw || '')}"`,
        `tokens=[${Array.from(frequencyFilterTokens).join(', ')}]`,
        `filter_active=${hasFrequencyFilterActive}`,
        `groups=${groups.length}`,
        `current_group="${currentGroup?.name || 'n/a'}"`,
        `current_group_index=${state.g}`,
        `current_variant_index=${state.v}`,
        `current_plan_index=${state.s}`,
        `current_group_is_frequency_mode=${groupHasFrequencyOptions(currentGroup)}`,
        `current_plans_all=[${currentPlanNames.join(' | ')}]`,
        `current_plans_visible=[${selectedPlanNames.join(' | ')}]`,
        ...groupLines
      ];

      debugEl.textContent = lines.join('\n');
    };
    const frequencyPricesDiffer = (group) => {
      if (!groupHasFrequencyOptions(group)) return false;
      const prices = new Set(
        (group.items || [])
          .map(item => item?.plans?.[0]?.price)
          .filter(price => Number.isFinite(price))
      );
      return prices.size > 1;
    };
    const moveStrikethroughToFrequencyButtons = () => {
      const g = curGroup();
      return hasRealGroups && frequencyPricesDiffer(g);
    };
    const hasMultiSubsForCurrent = () => curPlans().length > 1;
    const curPlan = () => {
      const plans = curPlans();
      if (!plans.length) return null;
      state.s = clamp(state.s, plans.length);
      return plans[state.s] || plans[0] || null;
    };

    // ---------- badge helpers ----------
    const calcSavingsPercent = (originalPrice, subPrice) => {
      if (!originalPrice || !subPrice || originalPrice <= subPrice) return 0;
      return Math.round(((originalPrice - subPrice) / originalPrice) * 100);
    };

    const getBadgeType = (index, total) => {
      if (total < 2) return null;
      const middleIndex = Math.floor(total / 2);
      const lastIndex = total - 1;
      if (index === middleIndex) return 'popular';
      if (index === lastIndex && index !== middleIndex) return 'value';
      return null;
    };

    const getBadgeConfig = ({ index, total, label, savings }) => {
      const normalized = String(label || '').toLowerCase();

      if (hasFrequencyFilterActive) {
        return null;
      }

      if (/(^|[^0-9])1\s*month(s)?([^0-9]|$)/.test(normalized)) {
        return { type: 'popular', text: `Most Popular - Save ${savings}%` };
      }

      if (/(^|[^0-9])3\s*month(s)?([^0-9]|$)/.test(normalized)) {
        return { type: 'value', text: `Best Results - Save ${savings}%` };
      }

      const fallbackType = getBadgeType(index, total);
      if (!fallbackType) return null;

      return {
        type: fallbackType,
        text: fallbackType === 'popular'
          ? `Most Popular - Save ${savings}%`
          : `Best Value - Save ${savings}%`
      };
    };

    const updateHeaderBadge = () => {
      const it = curItem();
      const g = curGroup();
      const plans = curPlans();
      const basePrice = it ? (priceByVariantId[it.variantId]?.compare || priceByVariantId[it.variantId]?.price) : null;
      const currentPlan = curPlan();

      const groupHasMultipleFrequencies = g && g.items && g.items.length > 1 && g.items.every(item => item.plans && item.plans.length === 1);

      [els.subPricesVariant, els.subPricesSub].forEach(container => {
        if (!container) return;

        const existing = $('[data-tcm-header-badge]', container);
        if (existing) existing.remove();

        const shouldShowBadge = state.mode === 'sub' && currentPlan && (
          plans.length > 1 || groupHasMultipleFrequencies
        );

        if (!shouldShowBadge) return;

        if (!hasRealGroups) {
          let pricesDiffer = false;
          if (groupHasMultipleFrequencies) {
            pricesDiffer = frequencyPricesDiffer(g);
          } else if (plans.length > 1) {
            const uniquePlanPrices = new Set(
              plans.map(plan => plan?.price).filter(price => Number.isFinite(price))
            );
            pricesDiffer = uniquePlanPrices.size > 1;
          }
          if (!pricesDiffer) {
            const savings = calcSavingsPercent(basePrice, currentPlan.price);
            const badge = document.createElement('span');
            badge.className = 'tcm-header-badge d-block text-center mb-3 text-uppercase bg-play park p-1 w-100 tcm-header-badge--save-only';
            badge.setAttribute('data-tcm-header-badge', '');
            badge.textContent = `Save ${savings}%`;
            container.insertBefore(badge, container.firstChild);
            return;
          }
        }

        const totalOptions = groupHasMultipleFrequencies ? g.items.length : plans.length;
        const currentIndex = groupHasMultipleFrequencies ? state.v : state.s;

        const savings = calcSavingsPercent(basePrice, currentPlan.price);
        const currentLabel = groupHasMultipleFrequencies
          ? (g.items[state.v]?.plans?.[0]?.name || g.items[state.v]?.label || '')
          : (plans[state.s]?.name || '');
        const badgeConfig = getBadgeConfig({
          index: currentIndex,
          total: totalOptions,
          label: currentLabel,
          savings
        });
        if (!badgeConfig) return;

        const badge = document.createElement('span');
        badge.className = `tcm-header-badge d-block text-center mb-3 text-uppercase bg-play park p-1 w-100 tcm-header-badge--${badgeConfig.type}`;
        badge.setAttribute('data-tcm-header-badge', '');
        badge.textContent = badgeConfig.text;
        container.insertBefore(badge, container.firstChild);
      });
    };

    // ---------- render (once) ----------
    function renderStatic() {
      if (els.groupContainer) {
        show(els.groupContainer, showGroups);
      }

      if (els.groupList) {
        show(els.groupList, showGroups);
        els.groupList.innerHTML = groups.map((g, i) => {
          const firstVariantId = g.items[0]?.variantId;
          const iconFile = firstVariantId ? (variantMeta[firstVariantId]?.icon || '') : '';
          const iconHTML = iconFile
            ? `<img src="${iconFile}" alt="" class="tcm-option-icon" aria-hidden="true" width="22" height="22" />`
            : `<span class="tcm-option-icon" aria-hidden="true">${BAG_SVG}</span>`;

          return `
            <li>
              <button class="${btnClass({ isFrequency: false })}" type="button" data-g="${i}">
                <span class="tcm-group-title" style="flex: 1 1 auto; text-align: left; min-width: 0;">${g.name}</span>
                ${iconHTML}
                <span class="tcm-group-price" data-gprice style="display: none;"></span>
              </button>
            </li>
          `;
        }).join('');
      }

      if (els.oneTimeList) {
        els.oneTimeList.innerHTML = `
          <li>
            <button class="${btnClass({ isFrequency: false })}" type="button" data-one="1">
              <span class="tile-title tcm-option-label">Or one off delivery</span>
              <span class="tile-price tcm-option-price" data-oneprice></span>
            </button>
          </li>
        `;
      }
    }

    // ---------- UI updates ----------
    function setHiddenInputs() {
      const it = currentPurchaseItem();
      if (!it) return;

      els.variantId.value = it.variantId;

      if (state.mode === 'one' || !hasAnyPlans) {
        els.sellingPlan.value = '';
        return;
      }

      const plans = it.plans || [];
      if (!plans.length) {
        els.sellingPlan.value = '';
        return;
      }

      els.sellingPlan.value = (plans.length === 1 ? plans[0] : curPlan())?.id || '';
    }

    function paintHeaders(onePrice, subPrice, compare) {
      const paint = (block) => {
        if (!block) return;
        const o = $('[data-tcm-sub-original]', block);
        const d = $('[data-tcm-sub-discount]', block);

        if (d) d.textContent = subPrice != null ? money(subPrice) : '';

        if (o && compare != null && subPrice != null && compare > subPrice) {
          if (moveStrikethroughToFrequencyButtons()) {
            show(o, false);
          } else {
            o.textContent = money(compare);
            show(o, true);
          }
        } else if (o) {
          show(o, false);
        }
      };

      const subListVisible = !!(els.subList && els.subList.style.display !== 'none' && els.subList.innerHTML.trim());
      const dualSubscribeBlocks = subscribeLayoutMode === 'single_variants_then_frequencies';
      show(els.subPricesVariant, hasAnyPlans && !dualSubscribeBlocks && !subListVisible);
      show(els.subPricesSub, hasAnyPlans && (dualSubscribeBlocks || subListVisible || !els.subscribeVariantContainer));

      paint(els.subPricesVariant);
      paint(els.subPricesSub);

      const it = curItem();
      const supplyText = it ? (variantMeta[it.variantId]?.supply || '') : '';
      [els.subPricesVariant, els.subPricesSub].forEach(container => {
        if (!container) return;
        const supplyEl = $('[data-tcm-supply]', container);
        if (supplyEl) supplyEl.textContent = supplyText;
      });
    }

    function paintPricesAndAtc() {
      const it = curItem();
      const purchaseItem = currentPurchaseItem();
      const oneTimeItem = findOneTimeItemForGroup(curGroup()) || purchaseItem;
      if (!it || !purchaseItem || !oneTimeItem) return;

      const selectedVp = priceByVariantId[it.variantId] || {};
      const purchaseVp = priceByVariantId[purchaseItem.variantId] || {};
      const oneTimeVp = priceByVariantId[oneTimeItem.variantId] || {};
      const onePrice = purchaseVp.price;
      const subCompare = (selectedVp.compare && selectedVp.compare > 0) ? selectedVp.compare : selectedVp.price;

      const onePriceEl = $('[data-oneprice]', els.oneTimeList);
      if (onePriceEl) {
        const displayPrice = oneTimeVp.price ?? onePrice;
        onePriceEl.textContent = money(displayPrice);
      }

      let subPrice = null;
      if (hasAnyPlans) {
        const plans = curPlans();
        if (plans.length === 1) subPrice = plans[0]?.price ?? null;
        else subPrice = curPlan()?.price ?? null;
      }

      paintHeaders(onePrice, subPrice, subCompare);

      if (!els.atcBtn.disabled) {
        const atcCents = (state.mode === 'sub' && hasAnyPlans) ? subPrice : onePrice;
        els.atcBtn.textContent = atcCents != null ? `Add to basket - ${money(atcCents)}` : 'Add to basket';
      }
    }

    function paintLists() {
      const g = curGroup();
      if (!g) return;

      if (els.variantList) {
        const showVariantList = g.items.length > 1;
        show(els.variantList, showVariantList);

        const areFrequencyOptions = g.items.length > 1 && g.items.every(it => {
          const plans = it.plans || [];
          return plans.length === 1 && plans[0]?.name;
        });
        const variantIndexes = filteredVariantIndexesForGroup(g);
        if (variantIndexes.length && !variantIndexes.includes(state.v)) {
          state.v = variantIndexes[0];
        }

        els.variantList.innerHTML = variantIndexes.map((itemIndex) => {
          const it = g.items[itemIndex];
          const plans = it.plans || [];
          const label = (hasAnyPlans && plans.length === 1)
            ? (plans[0]?.name ? formatFrequencyLabel(plans[0].name) : (it.label || 'Subscription'))
            : (it.label || 'Option');

          const shouldRenderVariantIcon = !areFrequencyOptions && hasRealGroups;
          const iconFile = variantMeta[it.variantId]?.icon || '';
          const iconHTML = shouldRenderVariantIcon
            ? (iconFile
              ? `<img src="${iconFile}" alt="" class="tcm-option-icon" aria-hidden="true" width="22" height="22" />`
              : `<span class="tcm-option-icon" aria-hidden="true">${BAG_SVG}</span>`)
            : '';

          return `
            <li>
              <button class="${btnClass({ isFrequency: areFrequencyOptions })}" type="button" data-v="${itemIndex}">
                <span class="tcm-option-label freq">${label}${areFrequencyOptions ? subscriptionNoteHtml : ''}</span>
                ${iconHTML}
                <span class="tcm-option-price" data-vprice></span>
              </button>
            </li>
          `;
        }).join('');
      }

      if (els.subList) {
        const plans = curPlans();
        const planIndexes = filteredPlanIndexes(plans);
        if (planIndexes.length && !planIndexes.includes(state.s)) {
          state.s = planIndexes[0];
        }
        const hasVisiblePlans = planIndexes.length > 0;

        show(els.subList, hasVisiblePlans);

        if (hasVisiblePlans) {
          els.subList.innerHTML = planIndexes.map((planIndex) => {
            const p = plans[planIndex];
            return `
            <li>
              <button class="${btnClass({ isFrequency: true })}" type="button" data-s="${planIndex}">
                <span class="tcm-option-label freq">${formatFrequencyLabel(p.name)}${subscriptionNoteHtml}</span>
                <span class="tcm-option-price" data-sprice></span>
              </button>
            </li>
          `;
          }).join('');
        }
      }

      if (els.groupList) {
        $$('.tcm-group-price[data-gprice]', els.groupList).forEach((el, idx) => {
          const grp = groups[idx];
          if (!grp) return;
          if (hasRealGroups && subscribeLayoutMode === 'grouped_frequencies') {
            show(el, false);
            el.textContent = '';
            return;
          }

          const showFromPrice = state.mode === 'one' && hasAnyPlans;
          let cents = null;
          let compare = null;

          if (showFromPrice) {
            const oneTimeItem = findOneTimeItemForGroup(grp);
            cents = oneTimeItem?.variantId ? (priceByVariantId[oneTimeItem.variantId]?.price ?? null) : null;
          } else if (subscribeLayoutMode === 'grouped_frequencies' && hasAnyPlans) {
            const selectedGroupItem = grp.items[clamp(state.v, grp.items.length)];
            const selectedVariantId = selectedGroupItem?.variantId || '';
            const selectedPlans = selectedGroupItem?.plans || [];
            const selectedPlan = selectedPlans.length > 1
              ? (selectedPlans[clamp(state.s, selectedPlans.length)] || selectedPlans[0])
              : selectedPlans[0];
            cents = selectedPlan?.price ?? null;
            compare = selectedVariantId
              ? ((priceByVariantId[selectedVariantId]?.compare && priceByVariantId[selectedVariantId]?.compare > 0)
                ? priceByVariantId[selectedVariantId]?.compare
                : priceByVariantId[selectedVariantId]?.price)
              : null;
          } else if (hasAnyPlans) {
            cents = grp.items.reduce((best, it) => {
              const plans = it.plans || [];
              const pick = plans[clamp(defaultSubIndex, plans.length)] || plans[0];
              const v = pick?.price ?? null;
              return (v != null && (best == null || v < best)) ? v : best;
            }, null);
          } else {
            cents = grp.items.reduce((best, it) => {
              const v = priceByVariantId[it.variantId]?.price ?? null;
              return (v != null && (best == null || v < best)) ? v : best;
            }, null);
          }

          if (showFromPrice && cents != null) {
            el.textContent = fromMoney(cents);
          } else if (subscribeLayoutMode === 'grouped_frequencies' && compare != null && cents != null && compare > cents) {
            el.innerHTML = `<s style="font-weight:400;font-size:0.86em;opacity:0.72;vertical-align:baseline;">${money(compare)}</s> <span style="vertical-align:baseline;">${money(cents)}</span>`;
          } else {
            el.textContent = money(cents);
          }
          show(el, cents != null);
        });
      }

      if (els.variantList) {
        const items = g.items;
        const currentPlanId = curPlan()?.id || null;
        const renderStrikeOnButtons = !hasRealGroups || moveStrikethroughToFrequencyButtons();
        const variantListRepresentsFrequencies = groupHasFrequencyOptions(g);

        $$('button[data-v]', els.variantList).forEach((btn) => {
          const variantIndex = Number(btn.dataset.v);
          const it = items[variantIndex];
          const priceEl = $('[data-vprice]', btn);
          if (!it || !priceEl) return;

          let cents = null;

          if (hasAnyPlans) {
            const plans = it.plans || [];
            if (plans.length === 1) cents = plans[0]?.price ?? null;
            else {
              const match = plans.find(p => p.id === currentPlanId) || plans[0];
              cents = match?.price ?? null;
            }
          } else {
            cents = priceByVariantId[it.variantId]?.price ?? null;
          }

          const showFromPrice = state.mode === 'one' && hasAnyPlans && !variantListRepresentsFrequencies;

          if (showFromPrice) {
            priceEl.textContent = fromMoney(cents);
            show(priceEl, cents != null);
          } else if (renderStrikeOnButtons && hasAnyPlans) {
            const compare = (priceByVariantId[it.variantId]?.compare && priceByVariantId[it.variantId]?.compare > 0)
              ? priceByVariantId[it.variantId]?.compare
              : priceByVariantId[it.variantId]?.price;

            if (compare != null && cents != null && compare > cents) {
              priceEl.innerHTML = `<s style="font-weight:400;font-size:0.86em;opacity:0.72;vertical-align:baseline;">${money(compare)}</s> <span style="vertical-align:baseline;">${money(cents)}</span>`;
            } else {
              priceEl.textContent = money(cents);
            }
            show(priceEl, true);
          } else {
            priceEl.textContent = money(cents);
            show(priceEl, cents != null);
          }
        });
      }

      if (els.subList && els.subList.style.display !== 'none') {
        const plans = curPlans();
        const visiblePlanIndexes = filteredPlanIndexes(plans);
        const visiblePlans = visiblePlanIndexes.map(idx => plans[idx]).filter(Boolean);
        const showSubButtonPrices = new Set(
          visiblePlans.map(p => p?.price).filter(price => Number.isFinite(price))
        ).size > 1;

        $$('button[data-s]', els.subList).forEach((btn) => {
          const el = $('[data-sprice]', btn);
          if (!el) return;
          const planIndex = Number(btn.dataset.s);
          const cents = plans[planIndex]?.price ?? null;
          el.textContent = money(cents);
          show(el, showSubButtonPrices && cents != null);
        });
      }
    }

    function paintActive() {
      const setActive = (el, on) => {
        if (!el) return;
        el.classList.toggle('is-active', on);
      };

      if (els.groupList) {
        $$('button[data-g]', els.groupList).forEach(btn => setActive(btn, Number(btn.dataset.g) === state.g));
      }

      const subListVisible = !!(els.subList && els.subList.style.display !== 'none' && els.subList.innerHTML.trim());
      const activeSubscribeContainer = (subListVisible || !els.subscribeVariantContainer)
        ? els.subscribeSubContainer
        : els.subscribeVariantContainer;
      const dualSubscribeBlocks = subscribeLayoutMode === 'single_variants_then_frequencies';

      if (dualSubscribeBlocks) {
        show(els.subscribeVariantContainer, true);
        show(els.subscribeSubContainer, true);
      } else if (subscribeLayoutMode === 'single_variants') {
        show(els.subscribeVariantContainer, true);
        show(els.subscribeSubContainer, false);
      } else {
        show(els.subscribeVariantContainer, !subListVisible);
        show(els.subscribeSubContainer, !!els.subscribeSubContainer);
      }

      [els.subscribeVariantContainer, els.subscribeSubContainer, els.oneTimeContainer].forEach(c => setActive(c, false));

      if (hasAnyPlans && dualSubscribeBlocks) {
        setActive(els.subscribeVariantContainer, state.mode === 'sub');
        setActive(els.subscribeSubContainer, state.mode === 'sub');
      } else if (hasAnyPlans) {
        setActive(activeSubscribeContainer, state.mode === 'sub');
      }
      setActive(els.oneTimeContainer, state.mode === 'one');

      if (els.subscribeBenefits?.length) {
        els.subscribeBenefits.forEach((benefitsEl) => {
          const parentContainer = benefitsEl.closest('[data-tcm-subscribe-container]');
          let shouldShow = hasAnyPlans && state.mode === 'sub' && (
            !parentContainer || parentContainer === activeSubscribeContainer
          );
          if (dualSubscribeBlocks) {
            shouldShow = hasAnyPlans && state.mode === 'sub' && parentContainer === els.subscribeSubContainer;
          }
          show(benefitsEl, shouldShow);
        });
      }

      if (els.variantList) {
        const g = curGroup();
        const items = g?.items || [];
        const variantTilesAreSizes = items.some(it => (it.plans || []).length > 1);

        $$('button[data-v]', els.variantList).forEach(btn => {
          const isSelected = Number(btn.dataset.v) === state.v;
          const on = variantTilesAreSizes
            ? isSelected
            : (isSelected && hasAnyPlans && state.mode === 'sub');
          setActive(btn, on);
        });
      }

      if (els.subList) {
        $$('button[data-s]', els.subList).forEach(btn => {
          setActive(btn, hasAnyPlans && state.mode === 'sub' && Number(btn.dataset.s) === state.s);
        });
      }

      if (els.oneTimeList) {
        $$('button[data-one]', els.oneTimeList).forEach(btn => setActive(btn, state.mode === 'one'));
      }
    }

    function render() {
      selectionIsOutOfStock = isSelectionOutOfStock();
      paintLists();
      renderFilterDebug();
      setHiddenInputs();
      paintPricesAndAtc();
      paintActive();
      updateHeaderBadge();
      updateAtcButtonState();
    }

    // ---------- scoped events ----------
    if (els.groupList) {
      els.groupList.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-g]');
        if (!btn) return;
        state.g = Number(btn.dataset.g);
        state.v = findPreferredVariantIndex(groups[state.g], defaultVariantIndex);
        state.s = defaultSubIndex;
        render();
      });
    }

    if (els.variantList) {
      els.variantList.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-v]');
        if (!btn) return;
        state.v = Number(btn.dataset.v);
        state.s = defaultSubIndex;
        if (hasAnyPlans && groupHasFrequencyOptions(curGroup())) state.mode = 'sub';
        render();
      });
    }

    if (els.subList) {
      els.subList.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-s]');
        if (!btn) return;
        state.s = Number(btn.dataset.s);
        if (hasAnyPlans) state.mode = 'sub';
        render();
      });
    }

    if (els.oneTimeList) {
      els.oneTimeList.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-one]');
        if (!btn) return;
        state.mode = 'one';
        render();
      });
    }

    // ---------- init this instance ----------
    renderStatic();
    render();

    // Register this instance for shared cart checking
    instances.push({ updateAtcButtonState, pd });

    if (enableAlreadyInCartLimit) {
      checkAllCarts();
    }
  }

  // ===== MAIN ENTRY =====
  function initNewForms() {
    document.querySelectorAll('.omni-product-form:not([data-tcm-init])').forEach(scope => {
      scope.setAttribute('data-tcm-init', '1');
      initProductForm(scope);
    });
  }

  // Set up shared cart listeners only once
  if (!window.__TCM_SHARED_INIT__) {
    window.__TCM_SHARED_INIT__ = true;
    setupCartListeners();
  }

  // Expose for re-invocation when script is loaded multiple times
  window.__TCM_INIT_FORMS__ = initNewForms;

  // Init all current forms
  initNewForms();
})();
